import i18next from 'i18next'
import toast from 'react-hot-toast'

/**
 * ERREURS D'API : UN SEUL TOAST, ET SA CAUSE (#266).
 *
 * L'intercepteur global (`services/api.ts`, zone gelée) toaste déjà TOUTE
 * erreur d'une requête non marquée `X-Background-Request` avec le message
 * brut du serveur. Les pages et les hooks ajoutaient le leur par-dessus
 * (« Failed to create campaign ») : deux bulles pour une seule erreur.
 *
 * La convention : UN point d'affichage par erreur.
 *   · requête ordinaire ⇒ l'intercepteur a parlé, l'appelant se tait ;
 *   · requête marquée `X-Background-Request` (`apiBackground`, ou en-tête posé
 *     par le service) ⇒ l'intercepteur se tait, l'appelant toaste SON message,
 *     suivi de la cause rendue par le serveur quand il y en a une.
 * `toastApiError` applique les deux branches ; `wasToastedGlobally` reproduit
 * le prédicat de l'intercepteur — sa parité avec le VRAI handler est gardée
 * par `apiError.spec.ts`.
 *
 * ⚠️ Les prédicats sont RECOPIÉS ici, pas importés de `services/api` : des
 * dizaines de specs doublent ce module par `vi.mock('…/services/api', () =>
 * ({ api }))`, et une fonction absente de la doublure ferait jeter
 * `toastApiError` dans leurs chemins d'erreur (registre, famille LOT-229).
 */

/** Miroir de `isTrialGatingError` (`services/api.ts`) : 403 du guard d'essai. */
function isTrialGating(error: unknown): boolean {
  const code = (error as { response?: { data?: { code?: unknown } } } | null)?.response?.data?.code
  return typeof code === 'string' && code.startsWith('TRIAL_')
}

/** Miroir de `isTenantPausedError` (`services/api.ts`) : plateau en pause. */
function isTenantPaused(error: unknown): boolean {
  const response = (error as { response?: { status?: unknown; data?: { code?: unknown } } } | null)?.response
  return response?.status === 403 && response?.data?.code === 'TENANT_PAUSED'
}

interface ApiErrorShape {
  config?: { headers?: Record<string, unknown> }
  response?: { status?: number; data?: { message?: unknown } }
}

/** Message rendu par le serveur (`message`, chaîne ou liste de validation), sinon `null`. */
export function apiErrorCause(error: unknown): string | null {
  const message = (error as ApiErrorShape | null)?.response?.data?.message
  if (typeof message === 'string') return message.trim() || null
  if (Array.isArray(message)) {
    const parts = message.filter((part): part is string => typeof part === 'string' && part.trim() !== '')
    return parts.length > 0 ? parts.join(' · ') : null
  }
  return null
}

/** Vrai si l'intercepteur global a DÉJÀ toasté cette erreur (miroir de `api.ts`). */
export function wasToastedGlobally(error: unknown): boolean {
  const shape = error as ApiErrorShape | null
  const config = shape?.config
  // Pas une erreur de requête axios (exception locale, annulation) : personne n'a parlé.
  if (!config) return false
  if (config.headers?.['X-Background-Request']) return false
  if (shape?.response?.status === 401) return false
  if (isTrialGating(error) || isTenantPaused(error)) return false
  return true
}

/**
 * Toaste `message` (déjà traduit) suivi de la cause serveur — sauf si
 * l'intercepteur a déjà affiché cette erreur.
 */
export function toastApiError(error: unknown, message: string): void {
  if (wasToastedGlobally(error)) return
  const cause = apiErrorCause(error)
  toast.error(cause ? i18next.t('common:errors.withCause', { message, cause }) : message)
}
